// src/pages/trading-dashboard/index.jsx
// Add an error notification for CoinMarketCap API key

import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import MarketTicker from './components/MarketTicker';
import BetEntry from './components/BetEntry';
import MarketChart from './components/MarketChart';
import OrderBook from './components/OrderBook';
import RecentTrades from './components/RecentTrades';
import ActiveBets from './components/ActiveBets';
import BetHistory from './components/BetHistory';
import PortfolioBalances from './components/PortfolioBalances';
import QuickActions from './components/QuickActions';
import { getPredictionMarkets, getMarketDetails } from '../../utils/api';

const PredictionDashboard = () => {
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [activeTab, setActiveTab] = useState('active-bets');
  const [isLayoutCustomizing, setIsLayoutCustomizing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch initial markets data
  useEffect(() => {
    const fetchMarkets = async () => {
      setIsLoading(true);
      try {
        const response = await getPredictionMarkets('all', 10);
        if (response?.success) {
          setMarkets(response?.data);
          if (response?.data?.length > 0) {
            setSelectedMarket(response?.data?.[0]);
          }
          setError(null);
        } else {
          setError('Failed to load prediction markets');
        }
      } catch (err) {
        console.error('Failed to fetch markets:', err);
        setError('Unable to connect to prediction markets');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMarkets();
    
    // Refresh markets every 30 seconds
    const interval = setInterval(fetchMarkets, 30000);
    return () => clearInterval(interval);
  }, []);

  const bottomTabs = [
    { id: 'active-bets', label: 'Active Bets', icon: 'Clock', count: 3 },
    { id: 'bet-history', label: 'Bet History', icon: 'History', count: null },
    { id: 'portfolio', label: 'Portfolio', icon: 'PieChart', count: null }
  ];

  const handleMarketSelect = async (market) => {
    try {
      const response = await getMarketDetails(market?.id);
      if (response?.success) {
        setSelectedMarket(response?.data);
      }
    } catch (err) {
      console.error('Failed to fetch market details:', err);
      setSelectedMarket(market); // Fallback to basic market data
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background pt-20">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="text-text-secondary">Loading prediction markets...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background pt-20">
        <div className="px-6 py-6">
          <div className="bg-error-900 bg-opacity-20 border border-error-600 text-error-300 p-4 rounded-trading flex items-start backdrop-blur-sm">
            <Icon name="AlertTriangle" size={20} className="mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-body-semibold mb-1">Unable to Load Markets</h3>
              <p className="text-sm mb-2 text-error-400">{error}</p>
              <button
                onClick={() => window.location?.reload()}
                className="text-sm bg-gradient-primary text-white px-3 py-1 rounded-trading hover:opacity-80 transition-opacity glow-primary"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="px-6 py-6">
        {/* Market Ticker */}
        <div className="mb-6">
          <MarketTicker 
            markets={markets}
            selectedMarket={selectedMarket}
            onMarketSelect={handleMarketSelect}
          />
        </div>

        {/* Main Interface */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
          {/* Left Column - Bet Entry */}
          <div className="lg:col-span-3">
            <div className="bg-surface border border-border rounded-trading shadow-trading-card h-full backdrop-blur-sm">
              <BetEntry 
                selectedMarket={selectedMarket}
                isLoading={isLoading}
              />
            </div>
          </div>

          {/* Middle Column - Market Chart */}
          <div className="lg:col-span-6">
            <div className="bg-surface border border-border rounded-trading shadow-trading-card h-full backdrop-blur-sm">
              <MarketChart 
                selectedMarket={selectedMarket}
                isLoading={isLoading}
              />
            </div>
          </div>

          {/* Right Column - Order Book & Recent Trades */}
          <div className="lg:col-span-3 space-y-6">
            <div className="bg-surface border border-border rounded-trading shadow-trading-card backdrop-blur-sm">
              <OrderBook selectedMarket={selectedMarket} />
            </div>
            <div className="bg-surface border border-border rounded-trading shadow-trading-card backdrop-blur-sm">
              <RecentTrades selectedMarket={selectedMarket} />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-6">
          <QuickActions 
            selectedMarket={selectedMarket}
            markets={markets}
          />
        </div>

        {/* Bottom Section - Tabbed Interface */}
        <div className="bg-surface border border-border rounded-trading shadow-trading-card backdrop-blur-sm">
          {/* Tab Headers */}
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <div className="flex items-center space-x-1">
              {bottomTabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-trading font-body-medium text-sm transition-all duration-trading-fast ${
                    activeTab === tab?.id
                      ? 'bg-primary-900 text-primary-300 border border-primary-800 glow-primary' :'text-text-secondary hover:text-text-primary hover:bg-surface-hover hover:border-border-hover'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                  {tab?.count && (
                    <span className="bg-gradient-primary text-white text-xs px-2 py-0.5 rounded-full min-w-[20px] text-center glow-primary">
                      {tab?.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <button
              onClick={() => setIsLayoutCustomizing(!isLayoutCustomizing)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-trading font-body-medium text-sm transition-all duration-trading-fast ${
                isLayoutCustomizing
                  ? 'bg-accent-900 text-accent-300 border border-accent-800' :'text-text-secondary hover:text-text-primary hover:bg-surface-hover hover:border-border-hover'
              }`}
            >
              <Icon name="Layout" size={16} />
              <span className="hidden sm:inline">Customize</span>
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'active-bets' && <ActiveBets />}
            {activeTab === 'bet-history' && <BetHistory />}
            {activeTab === 'portfolio' && <PortfolioBalances />}
          </div>
        </div>

        {/* Layout Customization Overlay */}
        {isLayoutCustomizing && (
          <div className="fixed inset-0 bg-black bg-opacity-70 z-modal flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-surface border border-border rounded-trading shadow-trading-modal max-w-md w-full p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-heading-semibold text-text-primary">
                  Customize Layout
                </h3>
                <button
                  onClick={() => setIsLayoutCustomizing(false)}
                  className="p-2 rounded-trading hover:bg-surface-hover transition-colors duration-trading-fast"
                >
                  <Icon name="X" size={20} className="text-text-secondary" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 border border-border rounded-trading bg-surface-hover">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-body-medium text-sm text-text-primary">
                      Widget Positions
                    </span>
                    <Icon name="Move" size={16} className="text-primary" />
                  </div>
                  <p className="text-xs text-text-secondary">
                    Drag and drop widgets to customize your prediction dashboard
                  </p>
                </div>
                
                <div className="p-4 border border-border rounded-trading bg-surface-hover">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-body-medium text-sm text-text-primary">
                      Chart Settings
                    </span>
                    <Icon name="BarChart3" size={16} className="text-primary" />
                  </div>
                  <p className="text-xs text-text-secondary">
                    Configure market price charts and indicators
                  </p>
                </div>
                
                <div className="p-4 border border-border rounded-trading bg-surface-hover">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-body-medium text-sm text-text-primary">
                      Color Theme
                    </span>
                    <Icon name="Palette" size={16} className="text-primary" />
                  </div>
                  <p className="text-xs text-text-secondary">
                    Switch between light and dark prediction themes
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 mt-6">
                <button
                  onClick={() => setIsLayoutCustomizing(false)}
                  className="flex-1 bg-gradient-primary text-white px-4 py-2 rounded-trading font-body-medium text-sm hover:opacity-80 transition-opacity duration-trading-fast glow-primary"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setIsLayoutCustomizing(false)}
                  className="flex-1 bg-surface-hover text-text-primary px-4 py-2 rounded-trading font-body-medium text-sm hover:bg-border-hover border border-border transition-colors duration-trading-fast"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PredictionDashboard;